import React, { useRef, useState, useEffect } from 'react';
import { getSurveys, saveTemplate, getTemplate } from '../services/storage';
import { CardField, SurveyData } from '../types';
import { drawCardOnCanvas, downloadCanvas, AVAILABLE_FIELDS } from '../utils/cardUtils';
import { Upload, Sliders, Plus, Trash2, MousePointer2, Loader, Save, Check, AlertCircle } from 'lucide-react';

// Helper to compress image to safe Base64 size for Firestore (max 1MB doc size)
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // Reduce max width to 800px to ensure size stays well under 1MB
        const MAX_WIDTH = 800; 
        let width = img.width;
        let height = img.height;
        
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        if(ctx) {
             ctx.drawImage(img, 0, 0, width, height);
             // Use 0.6 quality for aggressive compression while maintaining readability
             const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
             resolve(dataUrl);
        } else {
             reject(new Error("Canvas context failed"));
        }
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

const CardGenerator: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [templateImg, setTemplateImg] = useState<string | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [fields, setFields] = useState<CardField[]>([]);
  const [selectedSurveyIndex, setSelectedSurveyIndex] = useState(0);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [savedTemplate, savedSurveys] = await Promise.all([
          getTemplate(), 
          getSurveys({ limitCount: 20 })
        ]);
        
        if (savedTemplate) {
          setTemplateImg(savedTemplate.backgroundImage);
          setFields(savedTemplate.fields);
        }
        setSurveys(savedSurveys);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (templateImg && canvasRef.current) {
      let previewData = {};
      
      if (surveys.length > 0) {
          const selectedSurvey = surveys[selectedSurveyIndex];
          // Aggregate all sections to support photos in different sections
          previewData = Object.keys(selectedSurvey).reduce((acc, key) => {
              if (typeof selectedSurvey[key] === 'object' && !Array.isArray(selectedSurvey[key])) {
                  return { ...acc, ...selectedSurvey[key] };
              }
              return acc;
          }, {});
      } else {
          previewData = AVAILABLE_FIELDS.reduce((acc, f) => ({...acc, [f.key]: `[${f.label}]`}), {});
      }
      
      drawCardOnCanvas(canvasRef.current, { backgroundImage: templateImg, fields }, previewData);

      // Draw selection indicator
      if (selectedFieldIndex !== null && fields[selectedFieldIndex]) {
        const ctx = canvasRef.current.getContext('2d');
        const field = fields[selectedFieldIndex];
        if (ctx) {
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = 2;
          ctx.strokeRect(field.x - 5, field.y - field.fontSize, 200, field.fontSize + 10);
        }
      }
    }
  }, [templateImg, fields, selectedSurveyIndex, surveys, selectedFieldIndex]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Please select an image under 5MB.");
        return;
      }
      setTemplateFile(file);
      
      // Immediate preview
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (evt.target?.result) setTemplateImg(evt.target.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (selectedFieldIndex === null || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    updateField(selectedFieldIndex, 'x', Math.round(x));
    updateField(selectedFieldIndex, 'y', Math.round(y));
  };

  const addField = () => {
    const newField = { 
      key: 'headName', 
      label: 'കുടുംബ നാഥൻ / നാഥ', 
      x: 50, 
      y: 100, 
      fontSize: 24, 
      color: '#000000' 
    };
    setFields([...fields, newField]);
    setSelectedFieldIndex(fields.length);
  };

  const removeField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
    if (selectedFieldIndex === index) setSelectedFieldIndex(null);
  };

  const updateField = (index: number, key: keyof CardField, value: any) => {
    const newFields = [...fields];
    const field = newFields[index];
    
    if (key === 'key') {
       const selected = AVAILABLE_FIELDS.find(f => f.key === value);
       if (selected) {
         field.key = selected.key;
         field.label = selected.label;
       }
    } else {
       // @ts-ignore
       field[key] = value;
    }
    setFields(newFields);
  };

  const saveConfig = async () => {
    if (!templateImg) return;
    setSaving(true);
    try {
      let finalImageUrl = templateImg;
      
      // If new file, compress and prepare Base64 string
      if (templateFile) {
         finalImageUrl = await compressImage(templateFile);
         
         // Safety check for size
         if (finalImageUrl.length > 900000) { // Approx 900KB
             throw new Error("Image is too complex to save. Please try a simpler or smaller background image.");
         }
         
         setTemplateImg(finalImageUrl);
         setTemplateFile(null);
      }

      // Save to Firestore (Base64 string + fields config)
      await saveTemplate({ backgroundImage: finalImageUrl, fields });
      alert('Template configuration saved successfully! You can now generate cards.');
    } catch (error: any) {
      console.error(error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center"><Loader className="animate-spin inline mr-2"/> Loading...</div>;

  return (
    <div className="flex flex-col lg:flex-row gap-6 pb-24 lg:pb-10 relative">
      {/* Settings Panel */}
      <div className="bg-white p-6 rounded-lg shadow-md space-y-6 lg:w-1/3 order-2 lg:order-1 h-fit">
        <h2 className="text-xl font-bold border-b pb-2 text-gray-800">Card Template Settings</h2>
        
        <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:bg-blue-100 transition-colors relative">
          {templateFile && (
            <div className="absolute top-2 right-2 bg-green-100 text-green-700 px-2 py-1 rounded text-xs flex items-center shadow-sm">
              <Check size={12} className="mr-1"/> New File Selected
            </div>
          )}
          <label className="cursor-pointer block w-full h-full">
            <Upload className="mx-auto text-blue-500 mb-2" size={32} />
            <span className="text-blue-700 font-semibold block">Upload Template Image</span>
            <span className="text-xs text-gray-500 block mt-1">(JPG/PNG. Will be auto-optimized)</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center border-b pb-2">
             <h3 className="font-semibold text-gray-700 flex items-center"><Sliders size={16} className="mr-2"/> Fields Configuration</h3>
             <button onClick={addField} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded flex items-center hover:bg-emerald-700 transition"><Plus size={14} className="mr-1"/> Add Field</button>
          </div>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {fields.map((field, idx) => (
            <div 
              key={idx} 
              className={`p-3 rounded text-sm relative border cursor-pointer transition-all ${selectedFieldIndex === idx ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
              onClick={() => setSelectedFieldIndex(idx)}
            >
              <button onClick={(e) => { e.stopPropagation(); removeField(idx); }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={16}/></button>
              
              <div className="flex items-center mb-2 text-xs text-gray-500 font-bold uppercase tracking-wider">
                {selectedFieldIndex === idx && <MousePointer2 size={12} className="mr-1 text-blue-600 animate-pulse" />}
                Field {idx + 1}
              </div>

              <div className="mb-3 pr-6">
                 <select 
                    className="w-full border border-gray-300 rounded p-1.5 bg-white text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
                    value={field.key}
                    onChange={(e) => updateField(idx, 'key', e.target.value)}
                 >
                    {AVAILABLE_FIELDS.map(f => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                 </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">X Position</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.x} onChange={e => updateField(idx, 'x', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Y Position</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.y} onChange={e => updateField(idx, 'y', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Size (px)</label>
                   <input type="number" className="border border-gray-300 rounded w-full p-1" value={field.fontSize} onChange={e => updateField(idx, 'fontSize', parseInt(e.target.value))} />
                </div>
                <div>
                   <label className="text-[10px] uppercase font-bold text-gray-500">Color</label>
                   <input type="color" className="border border-gray-300 rounded w-full h-7 p-0 cursor-pointer" value={field.color} onChange={e => updateField(idx, 'color', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
          {fields.length === 0 && <p className="text-gray-400 text-center text-sm py-4 italic">No fields added yet.</p>}
          </div>
          
          <button 
            onClick={saveConfig} 
            disabled={saving}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-bold hover:bg-emerald-700 shadow-sm transition-transform active:scale-95 flex justify-center items-center"
          >
             {saving ? <Loader className="animate-spin mr-2" size={20}/> : <Save className="mr-2" size={20}/>}
             {saving ? 'Compressing & Saving...' : 'Save Template Settings'}
          </button>
        </div>
      </div>

      {/* Preview Panel - Sticky on Desktop */}
      <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center lg:w-2/3 order-1 lg:order-2 h-fit overflow-hidden lg:sticky lg:top-4">
        <div className="w-full flex justify-between items-center mb-4 border-b pb-2">
            <h2 className="text-xl font-bold text-gray-800">Preview & Edit</h2>
            <div className="flex items-center space-x-2">
               <AlertCircle size={14} className="text-blue-500"/>
               <span className="text-xs text-gray-500">Changes are saved only when you click "Save Template Settings"</span>
            </div>
        </div>
        
        {surveys.length > 0 && (
          <div className="w-full mb-4 bg-gray-50 p-3 rounded border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Preview with Data (Showing recent 20):</label>
            <select className="w-full border border-gray-300 p-2 rounded bg-white" onChange={e => setSelectedSurveyIndex(parseInt(e.target.value))}>
              {surveys.map((s, i) => (
                <option key={s.id} value={i}>{s.sectionA.serialNo} - {s.sectionA.headName}</option>
              ))}
            </select>
          </div>
        )}

        <div className="relative border-2 border-gray-200 border-dashed shadow-sm rounded-lg mb-6 w-full bg-gray-50 overflow-auto min-h-[300px] flex items-center justify-center p-4">
             {!templateImg && (
                <div className="text-center p-10">
                    <p className="text-gray-400 italic mb-2">No template uploaded.</p>
                    <p className="text-xs text-gray-400">Please upload an image from the settings panel.</p>
                </div>
             )}
             <div className="inline-block relative">
                <canvas 
                  ref={canvasRef} 
                  className={`max-w-none h-auto cursor-crosshair block shadow-lg ${!templateImg ? 'hidden' : ''}`}
                  style={{ maxWidth: '100%' }}
                  onClick={handleCanvasClick}
                />
             </div>
        </div>
      </div>
    </div>
  );
};

export default CardGenerator;